import katex from "katex";
import { useMemo } from "react";

import { contientDesMaths, typstVersLatex } from "./typst";

/**
 * Macros du préambule maison (`enoncés_ds/preambule.tex`).
 *
 * Sans elles, KaTeX affiche la commande en rouge : `\np{22.4}` deviendrait
 * une erreur au milieu d'une phrase par ailleurs correcte.
 */
const MACROS: Record<string, string> = {
  "\\np": "#1",
  "\\RR": "\\mathbb{R}",
  "\\NN": "\\mathbb{N}",
  "\\ZZ": "\\mathbb{Z}",
  "\\QQ": "\\mathbb{Q}",
  "\\CC": "\\mathbb{C}",
  "\\ii": "\\vec{\\imath}",
  "\\jj": "\\vec{\\jmath}",
  "\\dashuline": "\\underline{#1}",
  "\\euro": "€",
};

// Segments mathématiques : $...$ en ligne, \[...\] hors ligne.
const DELIMITEURS = /(\$[^$]*\$|\\\[[\s\S]*?\\\])/g;

function composer(segment: string): { html: string } | null {
  const horsLigne = segment.startsWith("\\[");
  const source = horsLigne ? segment.slice(2, -2) : segment.slice(1, -1);
  if (!source.trim()) return null;

  // `throwOnError: false` compose ce qu'il peut et signale le reste en rouge
  // plutôt que de faire disparaître toute la ligne. `trust` reste à sa valeur
  // par défaut : ni \href ni \includegraphics ne sont exécutés.
  return {
    html: katex.renderToString(source, {
      macros: MACROS,
      displayMode: horsLigne,
      throwOnError: false,
      strict: false,
    }),
  };
}

/**
 * Compose les mathématiques d'un texte mêlant prose et formules.
 *
 * Les aperçus de questions viennent des énoncés du professeur : ils sont
 * truffés de `$...$` que l'interface affichait jusqu'ici tels quels.
 *
 * Deux syntaxes coexistent dans l'application, et c'est voulu : les énoncés
 * sont en LaTeX, les messages rédigés par Claude en Typst — c'est ce moteur
 * qui compose les bilans PDF. KaTeX ne connaît que le LaTeX : les seconds sont
 * donc traduits pour l'affichage, jamais à l'enregistrement.
 */
export default function Maths({
  texte,
  syntaxe = "latex",
}: {
  texte: string;
  syntaxe?: "latex" | "typst";
}) {
  const morceaux = useMemo(() => {
    const source = syntaxe === "typst" ? typstVersLatex(texte) : texte;
    return source.split(DELIMITEURS).map((segment, index) => {
      if (index % 2 === 0) return { type: "texte" as const, contenu: segment };
      const compose = composer(segment);
      return compose
        ? { type: "maths" as const, contenu: compose.html }
        : { type: "texte" as const, contenu: segment };
    });
  }, [texte, syntaxe]);

  return (
    <>
      {morceaux.map((morceau, index) =>
        morceau.type === "maths" ? (
          <span
            key={index}
            // Le HTML vient de KaTeX, produit à partir du TeX ; il n'y a pas
            // de chemin d'injection depuis le texte source.
            dangerouslySetInnerHTML={{ __html: morceau.contenu }}
          />
        ) : (
          <span key={index}>{morceau.contenu}</span>
        ),
      )}
    </>
  );
}

/**
 * Rendu composé d'un message rédigé par Claude, sous la zone où on le retouche.
 *
 * La zone de saisie garde la source Typst — c'est elle qui part au PDF, et la
 * traduire à l'enregistrement ferait diverger l'écran et la fiche de l'élève.
 * L'aperçu ne s'affiche que si le message porte une formule : ailleurs il
 * répéterait le texte pour rien.
 */
export function ApercuTypst({ texte }: { texte: string }) {
  if (!contientDesMaths(texte)) return null;
  return (
    <div className="apercu-maths" aria-label="Aperçu composé">
      <Maths texte={texte} syntaxe="typst" />
    </div>
  );
}
